import { JSX, createRef, RefObject } from "react";
import { TreeDataGrid as MonzaTreeDataGrid } from "@monza/toolkit/tables/tree-data-grid/TreeDataGrid";
import { ITreeDataGridProps as IMonzaTreeDataGridProps, ITreeDataGridColumn as IMonzaTreeDataGridColumn, IStandardDataGridTreeViewData, TreeDataGridRef } from "@monza/toolkit/tables/tree-data-grid/types";
import { ITableBaseColumn } from "@monza/toolkit/tables/types";
import { ColumnWidthUnits } from "@monza/toolkit/tables/enums";
import ThemeProvider from "@monza/toolkit/theme/ThemeProvider";
import DEFAULT_MONZO_THEME from "@monza/toolkit/theme/DEFAULT_THEME";
import { BaseComponent } from "../../Core/BaseComponent/BaseComponent";
import { CheckBox } from "../../Inputs/CheckBox/CheckBox";
import { Typography } from "../../Typography/Typography/Typography";
import { INavigationNode, ITreeLoadPromise } from "@monza/toolkit/trees/treeview/types";
import { ExpandState, HasChildren, LoadChildrenOn } from "@monza/toolkit/trees/treeview/enums";
import { deepCopy } from "@monza/common/helpers/helpers";
import { Diff } from "@iconics/core";
import "./Styles.scss";
import { ExpandedNodesHelper } from "./ExpandedNodesHelper";

export class TreeDataGrid<TNode extends object> extends BaseComponent<ITreeDataGridProps<TNode>, ITreeDataGridState>
{
    public static noDataLabel = '';

    private monzaRef: RefObject<TreeDataGridRef<TNode> | null> = createRef();
    private wrapperRef: RefObject<HTMLDivElement | null> = createRef();
    private syntheticRootId = crypto.randomUUID();
    private syntheticRoot = {} as TNode;

    // ids of children just handed to the toolkit by a lazy load; they are already inserted by the
    // toolkit itself, so the reconcile skips re-inserting them when they flow back in through `data`.
    private pendingLazyIds = new Set<string>();

    constructor(props: ITreeDataGridProps<TNode>)
    {
        super(props);
        this.state = {};
    }

    public componentDidMount(): void
    {
        this.restoreExpansion();
    }

    public componentDidUpdate(prevProps: ITreeDataGridProps<TNode>): void
    {
        if (prevProps.data !== this.props.data)
        {
            this.reconcile(prevProps.data, this.props.data);
        }

        this.restoreExpansion();
        if (prevProps.selectedIds !== this.props.selectedIds)
        {
            this.monzaRef.current?.refreshAllRows();
        }
    }

    /**
     * Bring the rendered tree in line with a new `data` array without rebuilding it, so expansion
     * state and lazily-loaded children survive. The flat `data` list is the single source of truth:
     * a node that appears is inserted, a node that disappears is removed, and a node whose object
     * reference changed is updated in place (or relocated when its parent changed). Consumers only
     * ever mutate `data`; they never touch the tree imperatively.
     */
    private reconcile(previous: TNode[], current: TNode[]): void
    {
        const { getNodeId, getParentNodeId } = this.props;

        // a node is "updated" when its object reference changed (the consumer replaced it)
        const diff = new Diff(previous, current, getNodeId, (a, b) => a === b).execute();

        diff.deletedItems.forEach(node => this.deleteNode(getNodeId(node)));

        diff.createdItems.forEach(node =>
        {
            // a node just delivered by a lazy load is already inserted by the toolkit; skip its echo
            if (this.pendingLazyIds.delete(getNodeId(node)))
            {
                return;
            }
            this.addNode(node);
        });

        const previousParentById = new Map(previous.map(node => [getNodeId(node), getParentNodeId(node)]));
        diff.updatedItems.forEach(({ after: node }) =>
        {
            const id = getNodeId(node);
            if (this.pendingLazyIds.delete(id))
            {
                return;
            }
            if (previousParentById.get(id) !== getParentNodeId(node))
            {
                this.moveNode(node);
            }
            else
            {
                this.updateNode(node);
            }
        });
    }

    private restoreExpansion(): void
    {
        // setTimeout(0) defers until the toolkit has finished rebuilding the tree
        setTimeout(() =>
        {
            // only restore nodes whose entire ancestor chain is itself expanded because expandNode() force-expands a node's entire ancestor chain
            const { data, getNodeId, getParentNodeId, expandedIds } = this.props;
            const nodeHelper = new ExpandedNodesHelper(data, i => getNodeId(i), i => getParentNodeId(i), expandedIds ?? []);
            const nodeIdsToExpand = (expandedIds ?? []).filter(id => nodeHelper.areAllAncestorsExpanded(id));

            for (const id of nodeIdsToExpand)
            {
                this.monzaRef.current?.expandNode(id);
            }
        }, 0);
    }

    private handleSelectionChange(rowId: string, isChecked: boolean): void
    {
        const selectionMode = this.props.selectionMode ?? 'multi';
        const currentlySelectedIds = this.props.selectedIds ?? [];
        let newSelectedIds: string[];

        if (isChecked)
        {
            newSelectedIds = (selectionMode === 'single' ? [rowId] : Array.from(new Set([...currentlySelectedIds, rowId])));
        }
        else
        {
            newSelectedIds = currentlySelectedIds.filter(i => i !== rowId);
        }

        this.props.onSelectionChange?.(newSelectedIds);
    }

    private handleNodeStateChanged(node: TNode, column: IMonzaColumn<TNode>, currentView: string[]): void
    {
        if ('expandState' in node && 'id' in node)
        {
            const navigationNode = node as unknown as INavigationNode<TNode>;
            const currentExpanded = this.props.expandedIds ?? [];

            switch (navigationNode.expandState)
            {
                case ExpandState.EXPANDED:
                    if (!currentExpanded.includes(navigationNode.id))
                    {
                        this.props.onExpandedChange?.([...currentExpanded, navigationNode.id]);
                    }
                    break;

                case ExpandState.COLLAPSED:
                    if (currentExpanded.includes(navigationNode.id))
                    {
                        this.props.onExpandedChange?.(currentExpanded.filter(i => i !== navigationNode.id));
                    }
                    break;
            }
        }

        if ('onNodeStateChanged' in column)
        {
            column.onNodeStateChanged?.(node, currentView);
        }
    }

    private cachedColumns: Array<IMonzaColumn<TNode>> | undefined;
    private get columns(): Array<IMonzaColumn<TNode>>
    {
        const { columns, showCheckBoxes, getNodeId, getParentNodeId } = this.props;
        if (!this.cachedColumns)
        {
            const checkboxColumn: ITableBaseColumn<TNode> =
            {
                title: '',
                getCellValue: i => getNodeId(i),
                template: i => <div onClick={e => e.stopPropagation()}>
                    <CheckBox
                        label=""
                        checked={this.props.selectedIds?.includes(i.value as string)}
                        onCheckedChange={c => this.handleSelectionChange(i.value as string, c.checked === true)}
                    />
                </div>,
                columnWidth: 60,
                columnWidthUnits: ColumnWidthUnits.Pixels,
            };

            const monzaColumns = columns.map((column, index) =>
            {
                if (index === 0)
                {
                    return {
                        ...column,
                        getNodeId: (i: TNode) => (i === this.syntheticRoot ? this.syntheticRootId : getNodeId(i)),
                        getParentNodeId: (i: TNode) => (i === this.syntheticRoot ? null : (getParentNodeId(i) || this.syntheticRootId)),
                        onNodeStateChanged: (node: TNode, currentView: string[]) => this.handleNodeStateChanged(node, column, currentView),
                    } as IMonzaColumn<TNode>;
                }
                else
                {
                    return column;
                }
            });

            this.cachedColumns = (showCheckBoxes === true ? [checkboxColumn, ...monzaColumns] : monzaColumns);
        }
        return this.cachedColumns;
    }

    private cachedMonzaData: TNode[] | undefined;
    private cachedRoots: TNode[] | undefined;
    private lastSeenData: TNode[] | undefined;

    /**
     * The data handed to the toolkit to seed the tree. Only root nodes are passed; everything below
     * is fetched lazily on expansion. The reference is kept stable once the grid has mounted with at
     * least one root, so appending lazily-loaded children or mutating nodes (both applied imperatively
     * by the reconcile) never makes the toolkit rebuild the tree and lose its expansion state.
     */
    private get data(): TNode[]
    {
        if (this.lastSeenData !== this.props.data)
        {
            this.lastSeenData = this.props.data;
            if (!this.cachedRoots || this.cachedRoots.length === 0)
            {
                this.cachedRoots = this.props.data.filter(node => !this.props.getParentNodeId(node));
                this.cachedMonzaData = [this.syntheticRoot, ...this.cachedRoots];
            }
        }
        return this.cachedMonzaData ?? [this.syntheticRoot];
    }

    public render(): JSX.Element
    {
        const {
            data, getNodeId, getParentNodeId, columns,
            showCheckBoxes, selectionMode, selectedIds, onSelectionChange,
            expandedIds, onExpandedChange,
            ...baseProps
        } = this.props;

        const monzaData = this.data;
        if (monzaData.length <= 1) // only the hidden synthetic root: no roots to show yet
        {
            return (
                <div
                    className="tree-data-grid"
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '2rem',
                    }}
                >
                    <Typography
                        role='body'
                        size='medium'
                    >
                        {TreeDataGrid.noDataLabel}
                    </Typography>
                </div>
            );
        }

        return (
            <div
                className="tree-data-grid"
                ref={this.wrapperRef}
            >
                <ThemeProvider
                    theme={DEFAULT_MONZO_THEME}
                >
                    <MonzaTreeDataGrid<TNode>
                        {...baseProps}
                        data={monzaData}
                        showRoot={false}
                        ref={this.monzaRef}
                        columns={this.columns}
                        loadChildrenOn={{
                                onlyOneLoadingRequestAtTheTime: true,
                                loadChildrenAsync: (node, targetId) => this.handleLoadChildren(node, targetId),
                                loadOn: LoadChildrenOn.Expansion
                        }}
                    />
                </ThemeProvider>
            </div>
        );
    }

    private async handleLoadChildren(node: INavigationNode<TNode>, targetId: string | undefined): Promise<ITreeLoadPromise<TNode>>
    {
        const children = (await this.props.onLoadChildren?.(node.node, targetId)) ?? [];
        // remember these so the reconcile does not re-insert them when they arrive back through `data`
        children.forEach(child => this.pendingLazyIds.add(this.props.getNodeId(child)));
        return {
            children: children,
            parent: node,
            callback: () => {}
        };
    }

    /**
     * Imperatively insert a node into the already-loaded tree without rebuilding it.
     * A node whose parent is a root (parent id null/empty) is added under the hidden synthetic root.
     *
     * The node is only inserted when its parent is currently rendered and its children have already
     * been loaded. If the parent's children are not loaded yet (or the parent is not rendered), the
     * insert is skipped so the toolkit still lazily fetches the full child set - including this node -
     * when the parent is expanded. Otherwise the parent would show this node as its only child.
     *
     * Note: the toolkit's own addNode does not wire up the parent back-link, which leaves the new row
     * orphaned (it renders detached and breaks the parent's expand/collapse), so the insert is done here.
     */
    private addNode(node: TNode, addAtFirstPlace = false): void
    {
        this.insertNavigationNode(this.toNavigationNode(node), node, addAtFirstPlace);
    }

    /**
     * Imperatively move an already-loaded node (and its loaded subtree) under a different parent
     * without rebuilding the tree.
     *
     * The existing node is fetched (with its loaded children) and moved as-is rather than re-created,
     * so the toolkit does not lazily reload the children. A lazy reload would re-register the children's
     * ids at their new depth and the toolkit throws "Node Id must be unique" because its internal flat
     * id->node map is never purged on delete. The node is deep copied first so deleting the original
     * does not disturb the moved subtree.
     */
    private moveNode(node: TNode, addAtFirstPlace = false): void
    {
        const grid = this.monzaRef.current;
        if (grid == null)
        {
            return;
        }

        const id = this.props.getNodeId(node);
        // getNodeById is typed as INavigationNode<TNode> but actually returns the internal wrapped node
        const original = grid.getNodeById(id) as unknown as INavigationNode<IStandardDataGridTreeViewData<TNode>> | null;
        const movedNode = (original != null ? deepCopy(original) : this.toNavigationNode(node));
        grid.deleteNode(id);
        // reflect any fields changed by the same edit, keeping the moved subtree (movedNode.children)
        movedNode.node = this.toNodeData(node);
        this.insertNavigationNode(movedNode, node, addAtFirstPlace);
    }

    /**
     * Inserts a navigation node under the parent resolved from the row, wiring up the parent back-link
     * and tree levels that the toolkit's own addNode leaves unset (an unwired node renders detached and
     * breaks the parent's expand/collapse). Skips the insert when the parent is not rendered or its
     * children are not loaded yet, so the toolkit still lazily fetches the full child set on expand.
     */
    private insertNavigationNode(newNode: INavigationNode<IStandardDataGridTreeViewData<TNode>>, node: TNode, addAtFirstPlace: boolean): void
    {
        const grid = this.monzaRef.current;
        if (grid == null)
        {
            return;
        }

        const parentId = this.props.getParentNodeId(node) || this.syntheticRootId;
        const parent = this.findParentNode(parentId);
        if (parent == null || parent.hasChildren === HasChildren.UNSPECIFIED)
        {
            return;
        }

        parent.children ??= [];
        if (parent.children.some(child => child.id === newNode.id))
        {
            return;
        }

        this.rewireSubtree(newNode, parent, (parent.treeLevel ?? 0) + 1);
        if (addAtFirstPlace)
        {
            parent.children.unshift(newNode);
        }
        else
        {
            parent.children.push(newNode);
        }
        parent.hasChildren = HasChildren.YES;

        grid.refreshTree();
    }

    /** Re-points a node (and its subtree) at a new parent, fixing the parent back-links and tree levels. */
    private rewireSubtree(node: INavigationNode<IStandardDataGridTreeViewData<TNode>>, parent: INavigationNode<IStandardDataGridTreeViewData<TNode>>, treeLevel: number): void
    {
        node.parent = parent;
        node.treeLevel = treeLevel;
        node.children?.forEach(child => this.rewireSubtree(child, node, treeLevel + 1));
    }

    /**
     * Resolves the rendered navigation node for a parent id. The hidden synthetic root is never a
     * rendered row, so it is reached via the parent of any rendered root-level node.
     */
    private findParentNode(parentId: string): INavigationNode<IStandardDataGridTreeViewData<TNode>> | undefined
    {
        // getData() is typed as TNode[] but actually returns the rendered navigation nodes
        const rendered = (this.monzaRef.current?.getData() ?? []) as unknown as Array<INavigationNode<IStandardDataGridTreeViewData<TNode>>>;
        if (parentId === this.syntheticRootId)
        {
            return rendered.find(n => n.treeLevel === 1)?.parent;
        }
        return rendered.find(n => n.id === parentId);
    }

    /**
     * Imperatively update a loaded node's row data in place (cell values and tree display name)
     * without rebuilding the tree. Only the row payload is replaced, so the node keeps its
     * expansion state and already-loaded children.
     */
    private updateNode(node: TNode): void
    {
        const id = this.props.getNodeId(node);
        const nodeUpdate = { node: this.toNodeData(node) } as INavigationNode<IStandardDataGridTreeViewData<TNode>>;
        this.monzaRef.current?.updateNode(id, nodeUpdate, true);
    }

    /** Imperatively remove a node (and its subtree) from the loaded tree without rebuilding it. */
    private deleteNode(id: string): void
    {
        this.monzaRef.current?.deleteNode(id);
    }

    private toNavigationNode(node: TNode): INavigationNode<IStandardDataGridTreeViewData<TNode>>
    {
        return {
            id: this.props.getNodeId(node),
            node: this.toNodeData(node),
            expandState: ExpandState.COLLAPSED,
            // UNSPECIFIED shows the expander so the node's own children can be lazily loaded on expand
            hasChildren: HasChildren.UNSPECIFIED,
        };
    }

    private toNodeData(node: TNode): IStandardDataGridTreeViewData<TNode>
    {
        // columns[0] is always the tree column (see the columns getter); only it carries getDisplayName
        const treeColumn = this.props.columns[0] as Partial<IMonzaTreeDataGridColumn<TNode>>;
        const id = this.props.getNodeId(node);
        return {
            row: node,
            treeData: {
                displayName: treeColumn.getDisplayName?.(node) ?? '',
                description: treeColumn.getDescription?.(node) ?? '',
                path: id,
                children: [],
            },
        };
    }
}

export interface ITreeDataGridProps<TNode extends object> extends Omit<IMonzaTreeDataGridProps<TNode>, 'showRoot' | 'columns' | 'ref' | 'loadChildrenOn'>
{
    columns: Array<ITreeDataGridColumn<TNode>>;
    getNodeId: (item: TNode) => string;
    getParentNodeId: (item: TNode) => string | null;
    showCheckBoxes?: boolean; 
    selectionMode?: SelectionMode;
    selectedIds?: string[];
    onSelectionChange?: (ids: string[]) => void;
    expandedIds?: string[];
    onExpandedChange?: (ids: string[]) => void;
    onLoadChildren?: (node: TNode, targetId: string | undefined) => Promise<Array<TNode>>
}

type IMonzaColumn<TNode extends object> = ITableBaseColumn<TNode> | IMonzaTreeDataGridColumn<TNode>;
export type ITreeDataGridColumn<TNode extends object> = ITableBaseColumn<TNode> | Omit<IMonzaTreeDataGridColumn<TNode>, 'getNodeId' | 'getParentNodeId'>;
export interface ITreeDataGridState { }
export type SelectionMode = 'single' | 'multi';
export * from "@monza/toolkit/tables/tree-data-grid/types";
export * from "@monza/toolkit/tables/types";
export * from "@monza/toolkit/tables/enums";
